import { QueryClient } from "@tanstack/react-query";
import { createTRPCClient, httpBatchStreamLink } from "@trpc/client";
import { createTRPCOptionsProxy } from "@trpc/tanstack-react-query";
import type { ReactNode } from "react";
import superjson from "superjson";
import { TRPCProvider } from "#/integrations/trpc/react";
import type { TRPCRouter } from "#/integrations/trpc/router";

const isServer = typeof window === "undefined";

/**
 * Only created in the browser. On the server, tRPC calls are made
 * in-process via `trpcRouter` directly (see getContext), so no HTTP
 * client is needed there — that would deadlock a single-worker server
 * fetching itself while still handling the request that triggered it.
 */
export const trpcClient = isServer
	? undefined
	: createTRPCClient<TRPCRouter>({
			links: [
				httpBatchStreamLink({
					transformer: superjson,
					url: "/api/trpc",
				}),
			],
		});

/**
 * `import.meta.env.SSR` is statically `false` in the client build, so this
 * branch — and the prisma-touching router it imports — is dead-code-eliminated
 * from the browser bundle instead of being pulled in like a static import would.
 */
const serverRouter = import.meta.env.SSR
	? (await import("#/integrations/trpc/router")).trpcRouter
	: undefined;

export function getContext() {
	const queryClient = new QueryClient({
		defaultOptions: {
			dehydrate: { serializeData: superjson.serialize },
			hydrate: { deserializeData: superjson.deserialize },
		},
	});

	const trpc = trpcClient
		? createTRPCOptionsProxy({
				client: trpcClient,
				queryClient,
			})
		: // `serverRouter` is only unset when running in the browser, where
			// `trpcClient` above is always set — so this branch is unreachable then.
			createTRPCOptionsProxy({
				// biome-ignore lint/style/noNonNullAssertion: guaranteed set on the server, see above
				router: serverRouter!,
				ctx: { session: null },
				queryClient,
			});

	return { queryClient, trpc };
}

export default function TanstackQueryProvider({
	children,
	context,
}: {
	children: ReactNode;
	context: ReturnType<typeof getContext>;
}) {
	const { queryClient } = context;

	if (!trpcClient) {
		return <>{children}</>;
	}

	return (
		<TRPCProvider trpcClient={trpcClient} queryClient={queryClient}>
			{children}
		</TRPCProvider>
	);
}
